import pluralize from "pluralize";
import { z } from "zod";
import { assertUnreachable } from "../helpers/utilities.js";
import {
  automationDeliveryProviderSchema,
  automationReportDefinitionSchema,
  getAutomationRecipientSelection
} from "./automation.js";
const draftRecipientSelectionSchema = z.object({
  teammate_ids: z.array(z.string()).describe("Directory ids of individually named people."),
  group_ids: z.array(z.string()).describe(
    "Directory ids of named groups \u2014 naming a group reaches every member."
  )
});
const draftRecipientsDestinationSchema = z.object({
  type: z.literal("recipients"),
  primary: draftRecipientSelectionSchema.describe(
    "The people the instructions say should receive this report. Leave empty rather than inventing an audience."
  ),
  subscribers: draftRecipientSelectionSchema.describe(
    "People copied on the report in addition to its primary recipients."
  )
});
const draftChannelsDestinationSchema = z.object({
  type: z.literal("channels"),
  channels: z.array(z.string()).describe(
    "Chat channel names exactly as the instructions write them. Channels never deliver over email."
  )
});
const draftDestinationSchema = z.union([
  draftRecipientsDestinationSchema,
  draftChannelsDestinationSchema
]);
const draftInstanceRecipientRoutingSchema = z.object({
  type: z.literal("instance_recipient"),
  subscribers: draftRecipientSelectionSchema.describe(
    "People copied on every report in addition to its private subject \u2014 the same set on each one, so a per-subject audience cannot be expressed here."
  )
});
const draftMappedEntryDestinationSchema = z.union([
  draftRecipientsDestinationSchema.omit({ subscribers: true }),
  draftChannelsDestinationSchema
]);
const draftGroupMappedRoutingSchema = z.object({
  type: z.literal("mapped"),
  entries: z.array(
    z.object({
      instance: z.object({
        type: z.literal("group"),
        group_id: z.string().describe("Directory id of the group this entry routes.")
      }),
      destination: draftMappedEntryDestinationSchema
    })
  ).describe(
    `One destination per group, for instructions like "post each team's report in its own channel". A group without an entry gets no delivery.`
  )
});
const draftGroupRoutingSchema = z.union([draftRecipientsDestinationSchema, draftGroupMappedRoutingSchema]).describe(
  "Deliver every group result to one or more named managers, or map each group to its own channel. A shared channel destination is not supported for group results."
);
const draftManagerMappedRoutingSchema = z.object({
  type: z.literal("mapped"),
  entries: z.array(
    z.object({
      instance: z.object({
        type: z.literal("manager"),
        manager_id: z.string().describe("Directory id of the manager this entry routes.")
      }),
      destination: draftMappedEntryDestinationSchema
    })
  ).describe(
    `One destination per manager, for instructions like "post each manager's report in their own channel". A manager without an entry gets no delivery.`
  )
});
const draftManagerRoutingSchema = z.union([
  draftRecipientsDestinationSchema,
  draftChannelsDestinationSchema,
  draftInstanceRecipientRoutingSchema,
  draftManagerMappedRoutingSchema
]);
const draftSynthesisSchema = z.union([
  z.object({
    group_by: z.literal("manager"),
    routing: draftInstanceRecipientRoutingSchema,
    selection: z.null().describe(
      "Always null: the prompt cannot narrow which managers get the summary \u2014 only the configured form can. Instructions that try land in unresolved_requests."
    )
  }).describe(
    "One summary per manager, written from their team's reports and delivered privately to them."
  ),
  z.object({
    group_by: z.literal("group"),
    routing: draftGroupRoutingSchema
  }).describe(
    "One summary per group, written from its members' reports and routed like per-group reports."
  )
]);
const draftTeammateSelectionSchema = z.union([
  z.object({ type: z.literal("all") }),
  z.object({
    type: z.literal("specific"),
    teammate_ids: z.array(z.string()).describe("Directory ids of individually named report subjects."),
    group_ids: z.array(z.string()).describe("Groups whose members are report subjects.")
  })
]);
const draftSharedReportDefinitionSchema = z.object({
  instances: z.object({ type: z.literal("shared") }),
  subjects: draftTeammateSelectionSchema.describe(
    "Whose calls the single report covers. Use all only when the instructions do not restrict the report to named people or groups."
  ),
  routing: draftDestinationSchema,
  synthesis: z.null()
}).describe(
  "A single report covering all or selected teammates' calls, read alike by whoever receives it. For digests and combined summaries; analyzing several people in ONE report is still this shape."
);
const draftPerTeammateReportDefinitionSchema = z.object({
  instances: z.object({
    type: z.literal("teammates"),
    selection: draftTeammateSelectionSchema.describe(
      "The teammates who each get their own report. A selected group's members each get a report."
    )
  }),
  routing: draftInstanceRecipientRoutingSchema,
  synthesis: draftSynthesisSchema.nullable().describe(
    "An optional second tier of summaries built from the per-teammate reports; null when the instructions ask for none."
  )
}).describe(
  "One report per teammate, each covering only that teammate's own calls and delivered privately to them."
);
const draftPerManagerReportDefinitionSchema = z.object({
  instances: z.object({
    type: z.literal("managers"),
    selection: z.union([
      z.object({ type: z.literal("all") }),
      z.object({
        type: z.literal("specific"),
        teammate_ids: z.array(z.string()).describe(
          "Directory ids of the named managers. Someone who manages no group gets no report."
        )
      })
    ])
  }),
  routing: draftManagerRoutingSchema,
  synthesis: z.null()
}).describe(
  "One report per manager, each covering that manager's team's calls. Normally delivered privately to each manager, but may instead go to named people, channels, or a per-manager mapping."
);
const draftPerGroupReportDefinitionSchema = z.object({
  instances: z.object({
    type: z.literal("groups"),
    selection: z.union([
      z.object({ type: z.literal("all") }),
      z.object({
        type: z.literal("specific"),
        group_ids: z.array(z.string())
      })
    ])
  }),
  routing: draftGroupRoutingSchema,
  synthesis: z.null()
}).describe(
  "One report per group, each covering that group's members' calls. Deliver them only to named managers or through a per-group mapping, such as each group's own channel."
);
const automationInterpretationDraftDefinitionSchema = z.union([
  draftSharedReportDefinitionSchema,
  draftPerTeammateReportDefinitionSchema,
  draftPerManagerReportDefinitionSchema,
  draftPerGroupReportDefinitionSchema
]);
const automationInterpretationDraftSchema = z.object({
  provider: automationDeliveryProviderSchema.describe(
    "The requested delivery provider. Use email when none is specified. Preserve an explicitly requested disconnected provider so compilation can apply and explain the email fallback."
  ),
  definition: automationInterpretationDraftDefinitionSchema.describe(
    "The shape of the agent's output and where each report goes."
  ),
  unresolved_requests: z.array(z.string()).describe(
    "One entry per instruction about structure or delivery that could not be expressed in the fields above \u2014 an unknown name, an unsupported shape, a disconnected provider. Each is reported to the agent's editors. Say what was asked and why it could not be honored."
  )
});
const resolvedAutomationRunInterpretationSchema = z.object({
  status: z.literal("resolved"),
  report_definition: automationReportDefinitionSchema,
  transport: z.object({ provider: automationDeliveryProviderSchema }),
  warnings: z.array(z.string())
});
const automationRunInterpretationSchema = z.discriminatedUnion(
  "status",
  [
    resolvedAutomationRunInterpretationSchema,
    z.object({
      status: z.literal("unresolved"),
      error: z.string()
    })
  ]
);
function formatNameList(names, maxNames = 5) {
  const shown = names.slice(0, maxNames);
  const overflow = names.length - shown.length;
  return overflow > 0 ? `${shown.join(", ")}, +${overflow} more` : shown.join(", ");
}
const REPORT_INSTANCE_NOUNS = {
  teammates: "teammate",
  managers: "manager",
  groups: "group"
};
function describeReportInstances(definition, names) {
  const { instances } = definition;
  if (instances.type === "shared") {
    return "One shared result";
  }
  const noun = REPORT_INSTANCE_NOUNS[instances.type];
  const groupNames = (ids) => ids.map((id) => names.groupNamesById.get(id) ?? "a group");
  const { selection } = instances;
  switch (selection.type) {
    case "all":
      return `One result per ${noun}`;
    case "specific": {
      const selected = getAutomationRecipientSelection(definition);
      const selectedNames = [
        ...selected.teammate_ids.map(
          (id) => names.teammateNamesById.get(id) ?? "a teammate"
        ),
        ...groupNames(selected.group_ids)
      ];
      return selectedNames.length > 0 ? `One result per selected ${noun} (${formatNameList(selectedNames)})` : `One result per selected ${noun}`;
    }
    default:
      return assertUnreachable(selection);
  }
}
function describeReportRouting(routing, provider) {
  switch (routing.type) {
    case "instance_recipient": {
      const subscriberCount = routing.subscribers.teammate_ids.length;
      return subscriberCount > 0 ? `delivered privately by ${provider}, copying ${pluralize("subscriber", subscriberCount, true)}` : `delivered privately by ${provider} to the person it is about`;
    }
    case "recipients": {
      const recipientCount = routing.primary.teammate_ids.length + routing.primary.group_ids.length;
      return recipientCount > 0 ? `sent by ${provider} to ${pluralize("recipient", recipientCount, true)}` : "sent to no one \u2014 the instructions name no recipients";
    }
    case "channels":
      return routing.channels.length > 0 ? `posted to ${routing.channels.map((channel) => channel.name ?? channel.id).join(", ")}` : "posted to no channel \u2014 none of the named channels were found";
    case "mapped":
      return `sent by ${provider} to ${pluralize("mapped destination", routing.entries.length, true)}`;
    default:
      return assertUnreachable(routing);
  }
}
function describeAutomationReportDefinition(definition, provider, names) {
  const synthesis = definition.synthesis ? `, plus a summary for each ${definition.synthesis.group_by === "manager" && definition.synthesis.selection !== void 0 ? "selected manager" : definition.synthesis.group_by}` : "";
  return `${describeReportInstances(definition, names)}, ${describeReportRouting(definition.routing, provider)}${synthesis}.`;
}
export {
  automationInterpretationDraftDefinitionSchema,
  automationInterpretationDraftSchema,
  automationRunInterpretationSchema,
  describeAutomationReportDefinition,
  resolvedAutomationRunInterpretationSchema
};
//# sourceMappingURL=automationInterpretation.js.map
